import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import api from '@/services/api';
import { Locale } from '@/services/localeService';
import localeService from '@/services/localeService';
import { Channel } from '@/services/channelService';
import channelService from '@/services/channelService';
import { paths } from '@/lib/apiPaths';
import { usePriceMetadata, Currency, PriceType } from './usePriceMetadata';

interface OrgSettings {
  id: number;
  name: string;
  default_locale: string;
  default_locale_ref?: Locale;
  default_channel: Channel | null;
  default_currency?: Currency | null;
  default_currency_id?: string | null;
  default_price_type?: PriceType | null;
  default_price_type_id?: number | null;
  number_format: 'dot' | 'comma';
  date_format: 'MDY' | 'DMY' | 'YMD';
}

interface UseOrgSettingsOptions {
  includePricing?: boolean;
}

export function useOrgSettings(options: UseOrgSettingsOptions = {}) {
  const { includePricing = true } = options; // Default to true for backward compatibility
  const { user } = useAuth();
  const orgId = user?.organization_id;

  // Get price metadata (currencies and price types) - only when needed
  const {
    currencies,
    priceTypes,
    loading: priceMetadataLoading,
    error: priceMetadataError,
    refetch: refetchPriceMetadata,
  } = usePriceMetadata({ enabled: includePricing });

  // Query organization settings directly using user's organization_id
  const {
    data: orgSettings,
    isLoading: isOrgLoading,
    error: orgError,
    refetch: refetchOrgSettings,
  } = useQuery<OrgSettings>({
    queryKey: ['organization', orgId],
    queryFn: async () => {
      if (!orgId) throw new Error('No organization ID available');
      const response = await api.get(`/api/organizations/${orgId}/`);
      return response.data;
    },
    enabled: !!orgId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Query available locales
  const {
    data: locales = [],
    isLoading: isLocalesLoading,
    error: localesError,
    refetch: refetchLocales,
  } = useQuery<Locale[]>({
    queryKey: ['locales'],
    queryFn: localeService.getLocales,
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: !!orgId,
  });

  // Query available channels
  const {
    data: channelsRaw = [],
    isLoading: isChannelsLoading,
    error: channelsError,
    refetch: refetchChannels,
  } = useQuery<Channel[]>({
    queryKey: ['channels'],
    queryFn: channelService.getChannels,
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: !!orgId,
  });

  // Ensure channels have consistent properties
  const channels = channelsRaw.map(channel => ({
    ...channel,
    // Use name as label if label is missing, or code as label if both are missing
    label: channel.label || channel.name || channel.code,
  }));

  // Find default locale object from the locales list
  const defaultLocaleObj =
    locales.find(locale => locale.code === orgSettings?.default_locale) || null;

  // Find default channel object
  const defaultChannelObj =
    channels.find(
      channel => orgSettings?.default_channel?.id && channel.id === orgSettings.default_channel.id
    ) || null;

  // Find default currency object
  const defaultCurrencyObj =
    currencies.find(
      currency =>
        currency.iso_code === orgSettings?.default_currency?.iso_code ||
        currency.iso_code === orgSettings?.default_currency_id
    ) || null;

  // Find default price type object
  const defaultPriceTypeObj =
    priceTypes.find(
      priceType =>
        priceType.id === orgSettings?.default_price_type?.id ||
        priceType.id === orgSettings?.default_price_type_id
    ) || null;

  return {
    orgSettings,
    defaultLocale: orgSettings?.default_locale || 'en_US',
    defaultLocaleObj,
    locales,
    channels,
    defaultChannel: orgSettings?.default_channel || defaultChannelObj || null,
    defaultChannelId: orgSettings?.default_channel?.id || defaultChannelObj?.id || null,
    // New pricing defaults
    currencies,
    priceTypes,
    defaultCurrency: orgSettings?.default_currency || defaultCurrencyObj || null,
    defaultCurrencyId: orgSettings?.default_currency_id || defaultCurrencyObj?.iso_code || null,
    defaultPriceType: orgSettings?.default_price_type || defaultPriceTypeObj || null,
    defaultPriceTypeId: orgSettings?.default_price_type_id || defaultPriceTypeObj?.id || null,
    isLoading: isOrgLoading || isLocalesLoading || isChannelsLoading || priceMetadataLoading,
    isChannelsLoading,
    error: orgError || localesError || channelsError || priceMetadataError,
    refetch: () => {
      refetchOrgSettings();
      refetchLocales();
      refetchChannels();
      // Also refetch price metadata
      refetchPriceMetadata();
    },
  };
}
